import {compact} from '@benchling/js-utils';
import produce from 'immer';
import React from 'react';
import {ReactFlowInstance} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doRectanglesOverlap} from 'lib/doRectanglesOverlap';
import {COMMON_NODE_WIDTH} from 'procedures/design/procedureMethodNodeCommonConsts';
import {createPlaceholderNode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {isFlowchartType} from 'processes/processes-platform/FeatureOptions';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import * as Logging from 'util/Logging';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {
  getCreateLogicNodeName,
  getLogicNodeCreationContext,
  getNewLogicNode,
} from './left-sidepanel/logicNodeCreationUtils';
import {
  getNodeForCreateNewTaskSchema,
  getTaskSchemaCreationContext,
} from './left-sidepanel/TaskNodeSidepanelSection';
import {getLogicNodeMetadata} from './logicNodeMetadata';
import {ProcessFlowchartEdgeType, ProcessFlowchartNodeType} from './processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  PlaceholderStaticNodeData,
  PlaceholderType,
} from './processFlowchartConfigEditorNodeTypes';
import {FlowchartNodeChange} from './processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';

export interface GetAddNodeOptionsArgs {
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  sourceNodeId: string;
  reactFlowInstance: ReactFlowInstance;
  singletonTaskSchemasByName: Record<string, ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  canAddExistingTaskSchema: boolean;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  didInitialFlowchartHaveChainedRouters: boolean;
  openSidepanelForNode: (nodeId: string) => void;
  openSidepanelForNodeWithField: (nodeId: string, fieldId: string) => void;
  onNodeChangesOnly: (
    nodeChangeGenerator: (flowchartConfig: FlowchartConfigWithMetadata) => Array<FlowchartNodeChange>
  ) => void;
  setSourceNodeAndNewNodeBeingAdded: (sourceNodeIdAndNewNode: [string, FlowchartNode]) => void;
}

export interface AddNodeOption {
  optionValue: string;
  nodeName: string;
  nodeIcon: BenchlingIconName | null;
  onSelect: () => void;
}

export const FALLBACK_NODE_HEIGHT = 100;

// The maximum number of times we will check for intersections between the node being added and
// existing nodes in the flowchart.
export const MAX_INTERSECTION_CHECKS = 200;

// The distance from the current position we will search for intersecting nodes.
export const ADD_NODE_SEARCH_DISTANCE = 100;

export const getAreNodesIntersecting = (
  reactFlowInstance: ReactFlowInstance,
  nodeBeingAdded: FlowchartNode,
  currentPosition: {x: number; y: number}
): boolean => {
  let intersectingNodes: Array<FlowchartNode> = [];
  try {
    intersectingNodes = reactFlowInstance.getIntersectingNodes({
      x: currentPosition.x,
      y: currentPosition.y,
      width: nodeBeingAdded.width ?? COMMON_NODE_WIDTH,
      // We do not know the height of the node until after it has been painted.
      // So we assume that the nodes have a fixed height so we can get a best guess for the position.
      // The nodes added by this dropdown are fairly "short" since we add either placeholder nodes or
      // logic nodes here and all of those nodes should be roughly the same height.
      height: nodeBeingAdded.height ?? FALLBACK_NODE_HEIGHT,
    });
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (error) {
    Logging.warn('Reactflow getIntersectingNodes failed. Falling back to manual collision detection.', {
      extra: {
        error,
        currentPosition,
        allNodes: reactFlowInstance.getNodes?.() || [],
      },
    });
    const newNodeRect = {
      position: currentPosition,
      width: nodeBeingAdded.width ?? COMMON_NODE_WIDTH,
      height: nodeBeingAdded.height ?? FALLBACK_NODE_HEIGHT,
    };

    const allNodes = reactFlowInstance.getNodes?.() || [];
    intersectingNodes = allNodes.filter((existingNode) => {
      if (existingNode.position == null) {
        return false;
      }

      const existingNodeRect = {
        position: existingNode.position,
        width: existingNode.width ?? COMMON_NODE_WIDTH,
        height: existingNode.height ?? FALLBACK_NODE_HEIGHT,
      };

      return doRectanglesOverlap(newNodeRect, existingNodeRect);
    });
  }
  return intersectingNodes.length > 0;
};

export function getAddNodeOptions({
  flowchartEditorConfigurator,
  sourceNodeId,
  reactFlowInstance,
  singletonTaskSchemasByName,
  rootTaskSchema,
  canAddExistingTaskSchema,
  didInitialFlowchartHaveChainedRouters,
  openSidepanelForNode,
  openSidepanelForNodeWithField,
  onNodeChangesOnly,
  setSourceNodeAndNewNodeBeingAdded,
}: GetAddNodeOptionsArgs): Array<AddNodeOption> | null {
  const sourceNode = reactFlowInstance.getNode(sourceNodeId) as FlowchartNode | undefined;
  assert(sourceNode != null, 'Source node not found');
  const edges = reactFlowInstance.getEdges();
  const hasOutgoingEdges = edges.some(
    (edge) => edge.source === sourceNodeId && edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW
  );
  if (
    !flowchartEditorConfigurator.hasFeature(
      ProcessFlowchartPlatformFeatureType.ALLOW_BRANCHING_TRANSITIONS
    ) &&
    hasOutgoingEdges
  ) {
    return null;
  }
  const sourceNodeType = sourceNode.data.nodeType;
  const position = sourceNode.position;
  // The new node should be added to the right of the source node.
  // We add a buffer of 1.5x the standard node width to keep the nodes separate. We do not do any kind of smart
  // autolayouting here as that would require recomputing positions for all nodes in the graph which could be
  // disruptive to users.
  const newPositionX = position.x + 1.5 * COMMON_NODE_WIDTH;
  // This is the initial position of the new node. We will adjust this position to avoid intersecting with existing
  // nodes after the node has been created.
  const newNodePosition = {
    x: newPositionX,
    y: position.y,
  };

  const allowedNodeExecutionTypes = flowchartEditorConfigurator.allowedNodeExecutionTypes.filter(
    (executionType) => !isFlowchartType(executionType)
  );
  const allowedLogicNodes: Array<{
    nodeType: ProcessFlowchartNodeType;
    icon: BenchlingIconName;
    isPresent: boolean;
  }> = compact([
    {
      nodeType: ProcessFlowchartNodeType.ROUTER_NODE,
      icon: getLogicNodeMetadata(ProcessFlowchartNodeType.ROUTER_NODE).icon,
      isPresent:
        // If the "initial flowchart" had chained routers (two or more routers connected to each other), we
        // allow adding new chained routers.
        // Otherwise, we do not allow adding chained routers.
        (didInitialFlowchartHaveChainedRouters || sourceNodeType !== ProcessFlowchartNodeType.ROUTER_NODE) &&
        flowchartEditorConfigurator.hasFeature(ProcessFlowchartPlatformFeatureType.ALLOW_ROUTERS),
    },
    {
      nodeType: ProcessFlowchartNodeType.STOP_NODE,
      icon: getLogicNodeMetadata(ProcessFlowchartNodeType.STOP_NODE).icon,
      isPresent:
        sourceNodeType === ProcessFlowchartNodeType.ROUTER_NODE &&
        flowchartEditorConfigurator.hasFeature(ProcessFlowchartPlatformFeatureType.ALLOW_STOP_NODES),
    },
  ]);

  const setActualPosition = (nodeBeingAdded: FlowchartNode): FlowchartNode => {
    // Finds the next open position for the node by checking for intersections at evenly spaced intervals
    // above and below the initial new node position.
    let currPosition = newNodePosition;
    let intersectionChecks = 0;
    while (intersectionChecks < MAX_INTERSECTION_CHECKS) {
      const isNodeIntersecting = getAreNodesIntersecting(reactFlowInstance, nodeBeingAdded, currPosition);
      if (!isNodeIntersecting) {
        break;
      }

      intersectionChecks++;
      const shouldFlipVerticalDirection = intersectionChecks % 2 === 1;
      const multiplier = Math.ceil(intersectionChecks / 2);
      const newYPosition =
        newNodePosition.y + ADD_NODE_SEARCH_DISTANCE * (shouldFlipVerticalDirection ? -1 : 1) * multiplier;
      currPosition = {
        ...currPosition,
        y: newYPosition,
      };
    }
    return produce(nodeBeingAdded, (draft) => {
      draft.position = currPosition;
    });
  };

  const options: Array<AddNodeOption> = [];
  for (const executionType of allowedNodeExecutionTypes) {
    const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(executionType);
    const {nodeName, nodeIcon} = adapter.getNewNodeDetails({
      rootTaskSchema,
      singletonTaskSchemaForExecutionType: null,
    });
    options.push({
      optionValue: `new-node-${executionType}`,
      nodeName,
      nodeIcon,
      onSelect: async () => {
        const creationContext = getTaskSchemaCreationContext(
          singletonTaskSchemasByName,
          executionType,
          rootTaskSchema
        );
        let newNode = await getNodeForCreateNewTaskSchema({
          ...creationContext,
          executionType,
          options: {
            onNodeHeaderClick: openSidepanelForNode,
            onFieldClick: openSidepanelForNodeWithField,
            onUpdateNodeData: (nodeChangeGenerator, saveImmediately, skipSettingDirtyState) => {
              onNodeChangesOnly(nodeChangeGenerator);
              return Promise.resolve(null);
            },
            // We do not include the getAddNodeOptionsForRightHandle option here since it relies on this function
            // being fully defined. Instead we rely on the caller to attach the function when the node is added
            // to the canvas.
          },
          position: newNodePosition,
        });
        if (newNode != null) {
          newNode = setActualPosition(newNode);
          setSourceNodeAndNewNodeBeingAdded([sourceNodeId, newNode]);
        }
      },
    });
  }

  if (canAddExistingTaskSchema) {
    options.push({
      optionValue: 'add-existing-task-schema',
      nodeName: 'Existing task schema',
      nodeIcon: ModelTypeConstants.PROCESS_TASK,
      onSelect: () => {
        const newPlaceholderNodeStaticData: PlaceholderStaticNodeData = {
          id: uuidv4(),
          nodeType: ProcessFlowchartNodeType.PLACEHOLDER_NODE,
          placeholderForNodeTypeData: {
            placeholderType: PlaceholderType.ADD_EXISTING_TASK_SCHEMA,
            placeholderForNodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
          },
          nodeName: 'Add Existing Task Schema',
          nodeIcon: ModelTypeConstants.PROCESS_TASK,
        };
        const newNode = setActualPosition(
          createPlaceholderNode(newPlaceholderNodeStaticData, newNodePosition, {
            onNodeHeaderClick: openSidepanelForNode,
            onFieldClick: openSidepanelForNodeWithField,
            onUpdateNodeData: (nodeChangeGenerator, saveImmediately, skipSettingDirtyState) => {
              onNodeChangesOnly(nodeChangeGenerator);
              return Promise.resolve(null);
            },
            // We do not include the getAddNodeOptionsForRightHandle option here since it relies on this function
            // being fully defined. Instead we rely on the caller to attach the function when the node is added
            // to the canvas.
          })
        );
        setSourceNodeAndNewNodeBeingAdded([sourceNodeId, newNode]);
      },
    });
  }

  for (const logicNode of allowedLogicNodes) {
    if (!logicNode.isPresent) {
      continue;
    }

    options.push({
      optionValue: logicNode.nodeType,
      nodeName: getCreateLogicNodeName(logicNode.nodeType),
      nodeIcon: logicNode.icon,
      onSelect: () => {
        const staticData = getLogicNodeCreationContext(logicNode.nodeType);
        const newNode = setActualPosition(
          getNewLogicNode(staticData, newNodePosition, {
            onNodeHeaderClick: openSidepanelForNode,
            onFieldClick: openSidepanelForNodeWithField,
            onUpdateNodeData: (nodeChangeGenerator, saveImmediately, skipSettingDirtyState) => {
              onNodeChangesOnly(nodeChangeGenerator);
              return Promise.resolve(null);
            },
            // We do not include the getAddNodeOptionsForRightHandle option here since it relies on this function
            // being fully defined. Instead we rely on the caller to attach the function when the node is added
            // to the canvas.
          })
        );
        setSourceNodeAndNewNodeBeingAdded([sourceNodeId, newNode]);
      },
    });
  }

  return options;
}

export function AddNodeDropdown({options}: {options: Array<AddNodeOption>}): JSX.Element {
  return (
    <Menu width={MenuWidth.FIT_CONTENT} scrollable={false}>
      {options.map(({optionValue, nodeName, nodeIcon, onSelect}) => (
        <Option key={optionValue} value={optionValue} icon={nodeIcon ?? undefined} onSelect={onSelect}>
          {nodeName}
        </Option>
      ))}
    </Menu>
  );
}
